/**
 * apiClient — cliente HTTP base do SPA.
 *
 * Anexa JWT (Authorization) e um `X-Correlation-Id` a cada request; trata
 * erros de forma genérica (sem vazar tecnologia interna). NÃO conhece rotas de
 * negócio — os hooks dos módulos (useVisitas, useExportacoes...) as consomem.
 *
 * Sessão: o JWT viaja no header `Authorization`. Esta é uma SPA pura com o SDK
 * do Cognito (sem BFF/token-handler server-side), portanto NÃO há cookie
 * `HttpOnly` de refresh e as requisições não enviam credenciais de cookie
 * (`credentials: 'omit'`); o refresh é feito pelo SDK em memória (requirements
 * Req 1 AC5). Caso um BFF seja introduzido, revisar para `credentials: 'include'`.
 *
 * Nenhum dado pessoal é logado (RN-M11-001): erros carregam apenas status e
 * correlationId.
 */

/** Erro de API com status HTTP e correlationId para diagnóstico. */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly correlationId: string,
    message = 'Falha na comunicação com o serviço.',
    /**
     * Corpo de erro já parseado (quando o serviço devolve JSON de erro), para
     * casos como 409 que carregam o estado atual no `error.details`. Nunca
     * contém dado pessoal (o backend não expõe PII em erros — RN-GLOBAL-004).
     */
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Provedor do token de sessão — injetado pelo AuthContext. */
export type TokenProvider = () => string | undefined;

/** Callback disparado quando a API responde 401 (RN-M1-002). */
export type UnauthorizedHandler = () => void;

export interface ApiClientOptions {
  readonly baseUrl: string;
  readonly getToken?: TokenProvider;
  /**
   * Invocado a cada resposta 401. O AuthProvider liga aqui o logout +
   * redirecionamento ao /login (RN-M1-002): sem refresh silencioso no backend.
   */
  readonly onUnauthorized?: UnauthorizedHandler;
}

function generateCorrelationId(): string {
  return crypto.randomUUID();
}

export class ApiClient {
  constructor(private readonly options: ApiClientOptions) {}

  /**
   * Executa um request JSON. Método genérico — a tipagem do retorno é dada pelo
   * chamador (hook do módulo). Sem endpoints de negócio embutidos.
   */
  public async request<TResponse>(
    path: string,
    init: RequestInit = {},
  ): Promise<TResponse> {
    const correlationId = generateCorrelationId();
    const headers = new Headers(init.headers);
    headers.set('Content-Type', 'application/json');
    headers.set('X-Correlation-Id', correlationId);

    const token = this.options.getToken?.();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    const response = await fetch(`${this.options.baseUrl}${path}`, {
      ...init,
      headers,
      credentials: 'omit',
    });

    if (!response.ok) {
      // 401: sessão inválida/expirada — dispara o handler (logout + redirect ao
      // login, RN-M1-002) antes de propagar o erro. Sem refresh silencioso.
      if (response.status === 401) {
        this.options.onUnauthorized?.();
      }
      // Tenta ler o corpo de erro (JSON) para casos que carregam contexto (ex.:
      // 409 com estadoAtual em error.details). Falha de parse não mascara o erro.
      let corpo: unknown;
      try {
        corpo = await response.clone().json();
      } catch {
        corpo = undefined;
      }
      throw new ApiError(response.status, correlationId, undefined, corpo);
    }

    if (response.status === 204) {
      return undefined as TResponse;
    }
    return (await response.json()) as TResponse;
  }
}
